import { HttpClient } from "@angular/common/http";
import { effect, inject, Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, Subject, tap } from "rxjs";
import { Contact, ContactResponse, PotentialContact } from "../models/contact";
import { Jurisdiction } from "../models/jurisdiction";
import { ScreenStore, UserStore } from "../stores/index";
import { ColDef, ICellRendererParams, RowClassParams } from "ag-grid-community";
import { CentsToDollarsPipe } from "../pipes/cents-to-dollars.pipe";

export class ContactCredit
{
	public userId: string;
	public creditsToAdd: number;

	public constructor(userId: string, creditsToAdd: number)
	{
		this.userId = userId;
		this.creditsToAdd = creditsToAdd;
	}
}
export class ContactsCreditTransfer
{
	public contactsCreditTransfers: ContactCredit[];

	public constructor(contactsCreditTransfers: ContactCredit[])
	{
		this.contactsCreditTransfers = contactsCreditTransfers;
	}
}

export class CreateInmateContact
{
	public inmateFirstName: string;
	public inmateLastName: string;
	public inmateBookingNumber: string;
	public inmateBookedDate: string;
	public inmateBirthDate: string;
	public inmateJurisdictionId: string;

	public constructor(
		inmateFirstName: string,
		inmateLastName: string,
		inmateBookingNumber: string,
		inmateBookedDate: string,
		inmateBirthDate: string,
		inmateJurisdictionId: string)
	{
		this.inmateFirstName = inmateFirstName;
		this.inmateLastName = inmateLastName;
		this.inmateBookingNumber = inmateBookingNumber;
		this.inmateBookedDate = inmateBookedDate;
		this.inmateBirthDate = inmateBirthDate;
		this.inmateJurisdictionId = inmateJurisdictionId;
	}
}

@Injectable()
export class SharedContactRepository implements OnDestroy
{
	private requestMapping: string = "contacts";
	private http: HttpClient;
	public ready: BehaviorSubject<boolean>;
	public userId: string | undefined | null;
	public screenSize: string;
	public isMobile: boolean = false;
	public ngDestroyed$: Subject<any> = new Subject();
	private centsToDollarsPipe: CentsToDollarsPipe;

	readonly userStore = inject(UserStore);
	readonly screenStore = inject(ScreenStore);
	public colDefs: ColDef[];

	public civilianColDefs: ColDef[] = [
		{ headerName: "First Name", field: "contactFirstName", filter: true },
		{ headerName: "Last Name", field: "contactLastName", filter: true },
		{ headerName: "Booking #", field: "contactEmailOrBookingNumber", filter: true },
		{ headerName: "Credits", field: "contactCredits", filter: true, cellRenderer: ((params: ICellRendererParams) => {
			return this.centsToDollarsPipe.transform(params.data.contactCredits)
		}) },
		{ headerName: "Send Message", field: "createDraft", cellRenderer: this.createDraftIconFunction, resizable: false,
			maxWidth: 175
		},
		{ headerName: "Give Credit", field: "addCredits", cellRenderer: this.addCreditsIconFunction, resizable: false,
			maxWidth: 175 }
	];

	public civilianNoInmatePortalColDefs: ColDef[] = [
		{ headerName: "First Name", field: "contactFirstName", filter: true },
		{ headerName: "Last Name", field: "contactLastName", filter: true },
		{ headerName: "Booking #", field: "contactEmailOrBookingNumber", filter: true },
		{ headerName: "Credits", field: "contactCredits", filter: true, cellRenderer: ((params: ICellRendererParams) => {
			return this.centsToDollarsPipe.transform(params.data.contactCredits)
		}) },
		{ headerName: "Send Message", field: "createDraft", cellRenderer: this.createDraftIconFunction, resizable: false,
			maxWidth: 175
		}
	];

	public mobileCivilianTableColDefs: ColDef[] = [
		{ headerName: "Name", field: "contactFirstName", filter: true },
		{ headerName: "Credits", field: "contactCredits", filter: true },
		{ headerName: "Message", field: "createDraft", cellRenderer: this.createDraftIconFunction, resizable: false,
			maxWidth: 175 }
	];

	public inmateTableColDefs: ColDef[] = [
		{ headerName: "First Name", field: "contactFirstName", filter: true },
		{ headerName: "Last Name", field: "contactLastName", filter: true },
		{ headerName: "Identifier", field: "contactEmailOrBookingNumber", filter: true },
		{ headerName: "Message", field: "createDraft", cellRenderer: this.createDraftIconFunction, resizable: false,
			maxWidth: 175 }
	];

	public constructor(http: HttpClient, centsToDollarsPipe: CentsToDollarsPipe)
	{
		this.centsToDollarsPipe = centsToDollarsPipe;
		this.http = http;
		this.ready = new BehaviorSubject<boolean>(false);

		effect(() => {
			this.userId = this.userStore.userId();
			const type: string = this.userStore.type();
			if (this.userId && type)
			{
				if (type === "CIVILIAN")
				{
					if (this.isMobile)
					{
						this.colDefs = this.mobileCivilianTableColDefs;
					}
					else
					{
						this.colDefs = this.civilianNoInmatePortalColDefs;
					}
				}
				else if (type === "INMATE")
				{
					this.colDefs = this.inmateTableColDefs;
				}
				this.tableReady();
			}
		})

		effect(() => {
			this.screenSize = this.screenStore.screenSize();
			if (this.screenSize === "MOBILE") {
				this.isMobile = true;
			}
			else {
				this.isMobile = false;
			}
		})
	}

	tableReady(): void {
		if (this.userId && this.colDefs) {
			this.ready.next(true);
		}
	}

	ngOnDestroy(): void
	{
		this.ngDestroyed$.next(true);
	}

	public fetchAll(): Observable<Contact[]>
	{
		return this.http.get<Contact[]>(`${this.requestMapping}/users/${this.userId}`).pipe(tap((contacts: Contact[]) => {
			let hasInmatePortal: boolean = false;
			for (const contact of contacts)
			{
				if (contact?.hasInmatePortal)
				{
					hasInmatePortal = true;
				}
			}
			if (hasInmatePortal)
			{
				this.colDefs = this.civilianColDefs;
			}
			else
			{
				this.colDefs = this.civilianNoInmatePortalColDefs;
			}
		}));
	}

	public getListedContacts(query: string): Observable<Contact[]>
	{
		return this.http.get<Contact[]>(`${this.requestMapping}/users/${this.userId}/list?query=${query}`);
	}

	public createContact(contactUserId: string): Observable<void>
	{
		return this.http.post<void>(`${this.requestMapping}/users/${contactUserId}`, {});
	}

	public createInmateContact(newInmateContact: CreateInmateContact): Observable<ContactResponse>
	{
		return this.http.post<ContactResponse>(`${this.requestMapping}/new-inmate`, newInmateContact);
	}

	public getPotentialJurisdictions(): Observable<Jurisdiction[]>
	{
		return this.http.get<Jurisdiction[]>(`${this.requestMapping}/potential-jurisdictions`);
	}

	public getContactsPage(query: string): Observable<Contact[]>
	{
		return this.http.get<Contact[]>(`${this.requestMapping}?query=${query}`);
	}

	public getPotentialContacts(query: string): Observable<PotentialContact[]>
	{
		return this.http.get<PotentialContact[]>(`${this.requestMapping}/search?query=${query}`);
	}

	public giveCreditsToInmates(transfers: ContactsCreditTransfer): Observable<void>
	{
		return this.http.put<void>(`${this.requestMapping}/transfer`, transfers);
	}

	public getCredits(row: ICellRendererParams): string {
		return this.centsToDollarsPipe.transform(row.data.contactCredits);
	}

	public createDraftIconFunction(row: ICellRendererParams): string
	{
		return row.data.hasBeenReleased ? "" : "<i class=\"fa-light fa-paper-plane\"></i>";
	}

	public rowClassFunction(row: RowClassParams<any>): string | string[] | undefined
	{
		return row.data.hasBeenReleased ? "disabled-row" : "";
	}

	public addCreditsIconFunction(row: ICellRendererParams): string
	{
		return row.data.hasInmatePortal ? "<i class=\"fa-light fa-envelope-open-dollar\"></i>" : "N/A";
	}
}
